import React, { useState } from "react";
import CopyField from "components/UI-lib/CopyField";
import FlatCard from "components/UI-lib/FlatCard";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/Graphs/Select";
import { Input } from "components/Graphs/Input";

const getOS = (): string => {
  if (navigator.userAgent.indexOf("Win") != -1) return "windows";
  if (navigator.userAgent.indexOf("Mac") != -1) return "darwin";
  if (navigator.userAgent.indexOf("Linux") != -1) return "linux";
  if (navigator.userAgent.indexOf("Android") != -1) return "android";
  if (navigator.userAgent.indexOf("like Mac") != -1) return "ios";
  return "unknown";
};
interface CliCommandsProps {
  name: string;
  resourceNoun: "instance" | "node";
  userId?: string;
}
const getDefaultOS = (): string => {
  const detectedOS = getOS();
  if (detectedOS === "windows") return "windows";
  if (detectedOS === "linux") return "linux";
  return "darwin";
};

const CliCommands: React.FC<CliCommandsProps> = ({
  name,
  resourceNoun,
  userId = "",
}) => {
  const [selectedEditor, setSelectedEditor] = useState<string>("cursor");
  const [selectedOS, setSelectedOS] = useState<string>(getDefaultOS());
  const [localPort, setLocalPort] = useState("");
  const [remotePort, setRemotePort] = useState("");
  const [copyDirection, setCopyDirection] = useState<string>("upload");
  const [copySourcePath, setCopySourcePath] = useState("");
  const [copyDestPath, setCopyDestPath] = useState("");
  const isValidPort = (port: string): boolean => {
    if (!port || port.trim() === "") return false;
    const portNum = parseInt(port, 10);
    return (
      !isNaN(portNum) &&
      portNum >= 1 &&
      portNum <= 65535 &&
      portNum.toString() === port.trim()
    );
  };

  const getInstallCommand = (osType: string): string => {
    if (osType === "darwin") {
      return "brew install brevdev/homebrew-brev/brev";
    }
    return `bash -c "$(curl -fsSL https://raw.githubusercontent.com/brevdev/brev-cli/main/bin/install-latest.sh)"`;
  };

  const getOSLabel = (osType: string): string => {
    if (osType === "darwin") return "macOS";
    if (osType === "linux") return "Linux";
    return "Windows (WSL)";
  };

  return (
    <div className="flex w-full flex-col items-stretch justify-start">
      <div className="flex flex-row items-center mb-3 mt-5">
        <h1 className="text-md font-medium text-primary dark:text-white mr-3">
          Install the CLI
        </h1>
        <Select value={selectedOS} onValueChange={setSelectedOS}>
          <SelectTrigger className="w-36 h-6 mt-1">
            <SelectValue placeholder="Choose your OS" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="darwin">macOS</SelectItem>
            <SelectItem value="linux">Linux</SelectItem>
            <SelectItem value="windows">Windows (WSL)</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <FlatCard
        className="mb-3 w-full bg-secondaryBg"
        noBackground={true}
        noBorder={true}
      >
        <h4 className="mb-1 text-sm text-primary">
          Run this in your {getOSLabel(selectedOS)} terminal
        </h4>
        <code>
          <CopyField
            value={getInstallCommand(selectedOS)}
            style={{ marginBottom: "2px" }}
            analytics={{
              userid: userId,
              eventLabel: "clicked CLI install steps",
            }}
          />
        </code>
        {selectedOS === "windows" && (
          <div className="text-sm text-gray-400 mt-2">
            Make sure you have WSL 2 installed and configured, virtualization
            enabled in your BIOS, and Ubuntu installed from the Microsoft Store.
          </div>
        )}
      </FlatCard>
      <h1 className="text-md mt-5 mb-3 font-medium text-primary dark:text-white">
        Login to your account
      </h1>
      <FlatCard
        className="mb-3 bg-secondaryBg"
        noBackground={true}
        noBorder={true}
      >
        <h4 className="mb-2 text-sm text-primary">Run this in your terminal</h4>
        <code>
          <CopyField
            value="brev login"
            style={{ marginBottom: "2px" }}
            analytics={{
              userid: userId,
              eventLabel: "brev login",
            }}
          />
        </code>
      </FlatCard>
      <h1 className="text-md mt-5 mb-3 font-medium text-primary dark:text-white">
        Open a terminal locally
      </h1>
      <FlatCard
        className="mb-3 bg-secondaryBg"
        noBackground={true}
        noBorder={true}
      >
        <h4 className="mb-2 text-sm text-primary">Run this in your terminal</h4>{" "}
        <code>
          <CopyField
            value={`brev shell ${name}`}
            style={{ marginBottom: "2px" }}
            analytics={{
              userid: userId,
              eventLabel: "brev shell",
            }}
          />
        </code>
      </FlatCard>

      <div className="flex flex-row items-center mb-3 mt-5">
        <h1 className="text-md font-medium text-primary dark:text-white mr-3">
          Open in Code Editor
        </h1>
        <Select value={selectedEditor} onValueChange={setSelectedEditor}>
          <SelectTrigger className="w-32 h-6 mt-1">
            <SelectValue placeholder="Choose your editor" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="cursor">Cursor</SelectItem>
            <SelectItem value="tmux">Tmux</SelectItem>
            <SelectItem value="code">VS Code</SelectItem>
            <SelectItem value="windsurf">Windsurf</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <FlatCard
        className="mb-3 bg-secondaryBg"
        noBackground={true}
        noBorder={true}
      >
        <div className="flex flex-row items-center">
          <h4 className="mb-2 text-sm text-primary mr-3">
            Run this in your terminal
          </h4>
        </div>
        <code>
          <CopyField
            value={`brev open ${name} ${selectedEditor}`}
            style={{ marginBottom: "2px" }}
            analytics={{
              userid: userId,
              eventLabel: `brev open ${selectedEditor}`,
            }}
          />
        </code>
      </FlatCard>

      <h1 className="text-md mt-5 mb-3 font-medium text-primary dark:text-white">
        Port Forward
      </h1>
      <FlatCard
        className="mb-3 bg-secondaryBg"
        noBackground={true}
        noBorder={true}
      >
        <h4 className="mb-2 text-sm text-primary">Configure port forwarding</h4>
        <div className="flex flex-row gap-2 mb-3 mt-2">
          <Input
            placeholder="Local port (e.g. 1234)"
            value={localPort}
            onChange={(e) => setLocalPort(e.target.value)}
            className="w-40 h-10"
          />
          <Input
            placeholder="Remote port (e.g. 8888)"
            value={remotePort}
            onChange={(e) => setRemotePort(e.target.value)}
            className="w-40 h-10"
          />
        </div>
        <h4 className="mb-2 text-sm text-primary">Run this in your terminal</h4>
        <code>
          <CopyField
            value={`brev port-forward ${name} -p ${isValidPort(localPort) ? localPort : "$local"}:${isValidPort(remotePort) ? remotePort : "$remote"}`}
            style={{ marginBottom: "2px" }}
            analytics={{
              userid: userId,
              eventLabel: "brev port-forward",
            }}
          />
        </code>
      </FlatCard>

      <div className="flex flex-row items-center mb-3 mt-5">
        <h1 className="text-md font-medium text-primary dark:text-white mr-3">
          Copy Files
        </h1>
        <Select value={copyDirection} onValueChange={setCopyDirection}>
          <SelectTrigger className="w-32 h-6 mt-1">
            <SelectValue placeholder="Direction" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="upload">Upload</SelectItem>
            <SelectItem value="download">Download</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <FlatCard
        className="mb-3 bg-secondaryBg"
        noBackground={true}
        noBorder={true}
      >
        <h4 className="mb-2 text-sm text-primary">
          {copyDirection === "upload"
            ? `Copy files from your local machine to the ${resourceNoun}`
            : `Copy files from the ${resourceNoun} to your local machine`}
        </h4>
        <div className="flex flex-row gap-2 mb-3 mt-2">
          <Input
            placeholder={
              copyDirection === "upload"
                ? "Local path (e.g. ./file.txt)"
                : "Remote path (e.g. /home/ubuntu/file.txt)"
            }
            value={copySourcePath}
            onChange={(e) => setCopySourcePath(e.target.value)}
            className="w-64 h-10"
          />
          <span className="flex items-center text-gray-500 dark:text-gray-400">
            to
          </span>
          <Input
            placeholder={
              copyDirection === "upload"
                ? "Remote path (e.g. /home/ubuntu/)"
                : "Local path (e.g. ./downloads/)"
            }
            value={copyDestPath}
            onChange={(e) => setCopyDestPath(e.target.value)}
            className="w-64 h-10"
          />
        </div>
        <h4 className="mb-2 text-sm text-primary">Run this in your terminal</h4>
        <code>
          <CopyField
            value={
              copyDirection === "upload"
                ? `brev copy ${copySourcePath || "$local_path"} ${name}:${copyDestPath || "$remote_path"}`
                : `brev copy ${name}:${copySourcePath || "$remote_path"} ${copyDestPath || "$local_path"}`
            }
            style={{ marginBottom: "2px" }}
            analytics={{
              userid: userId,
              eventLabel: `brev copy ${copyDirection}`,
            }}
          />
        </code>
      </FlatCard>
    </div>
  );
};

export default CliCommands;
